import { useDebounce } from "@repo/mfe-core/host";
import { useCallback, useMemo, useRef, useState } from "react";
import { useOwnerFiltersValues } from "../../../../../../../../hooks/useAssignOwners";

const usePaginatedFilterMenu = (field: string, selectedValues: string[]) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [hasMenuOpened, setHasMenuOpened] = useState(true);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const loadingRef = useRef(false);

  const payload = {
    page: "nhiOwnershipDialogueBox",
    searchText: debouncedSearchTerm,
    field,
  };

  const {
    data: options,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useOwnerFiltersValues(
    payload,
    hasMenuOpened, // Only fetch data when menu has been opened
  );

  const handleMenuScroll = useCallback(
    (event: React.UIEvent<HTMLUListElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;

      if (
        scrollHeight - scrollTop <= clientHeight + 5 &&
        hasNextPage &&
        !isFetchingNextPage &&
        !loadingRef.current
      ) {
        loadingRef.current = true;

        fetchNextPage()
          .then(() => {
            setTimeout(() => {
              loadingRef.current = false;
            }, 300); // wait for render
            console.info("PaginatedFilterMenu -> Next page fetched");
          })
          .catch((e) => {
            loadingRef.current = false;
            console.error(
              "PaginatedFilterMenu -> Error while calling next page",
              e,
            );
          });
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  const menuOptions = useMemo(() => {
    // Flatten pages from infinite query data structure
    const flattenedOptions =
      options?.pages?.flatMap((page) => page.values) ?? [];

    const mappedOptions = flattenedOptions.map((option) => ({
      label: option.name,
      value: option.id,
      isChecked: selectedValues.includes(option.id),
    }));

    // Sort checked items to the top
    return mappedOptions.sort((a, b) => {
      if (a.isChecked === b.isChecked) {
        return 0;
      }

      return a.isChecked ? -1 : 1;
    });
  }, [options?.pages, selectedValues]);

  /**
   * Handles menu expand/collapse events with smart caching logic.
   *
   * - When expanded: Always fetch data
   * - When collapsed with selections: Keep data cached for label display
   * - When collapsed without selections: Reset cache to fetch fresh data on next open
   */
  const handleMenuExpanded = useCallback(
    (isExpanded: boolean) => {
      if (isExpanded) {
        setHasMenuOpened(true);
      } else if (!Array.isArray(selectedValues) || !selectedValues.length) {
        setHasMenuOpened(false);
      }
      // Don't reset hasMenuOpened to false - keep data available for label display if user selected that filter
    },
    [selectedValues],
  );

  return {
    menuOptions,
    searchTerm,
    setSearchTerm,
    handleMenuScroll,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    handleMenuExpanded,
  } as const;
};

export default usePaginatedFilterMenu;
